import { HttpVerbs, UATConstants } from 'src/Common/Models/UATConstants';
import { CreateActionROBObject } from '../models/CreateActionROBObject';
import { MANAGE_ACTION_CONSTANTS } from 'src/manageAction/utils/manageActionConstants';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ServiceFactoryClientExecuteApi } from 'src/Common/Services/ServiceFactoryToExecuteApi';
import ErrorResponse from 'src/Models/ErrorResponse';

/** Configuration for a people-picker field that requires user validation */
export interface MandateFormPickerConfig {
  formFieldName: string;
  formFieldLabel: string;
  apiFieldName: string;
  allowedList?: string[];
}

/** Typed error thrown when the create API returns a non-OK response */
export interface CreateActionApiError {
  status: number;
  data: {
    detail?: string;
    errors?: Record<string, string[]>;
    [key: string]: any;
  };
}

/**
 * Builds the mandatePickerFields payload from the raidEntity fields,
 * matching the format used by ManageAction (RaidDetailsFooter.pickMandatePickerFields).
 */
const buildMandatePickerFields = (
  fields: Record<string, any>,
  mandateFormPickers: MandateFormPickerConfig[]
): string => {
  const mandatePickerString: Array<Record<string, any>> = [];

  mandateFormPickers.forEach((picker) => {
    if (fields[picker.apiFieldName]) {
      const inputObject: Record<string, any> = {};
      let pickerValue = fields[picker.apiFieldName];
      // Backend expects an array format
      if (!Array.isArray(pickerValue)) {
        pickerValue = [pickerValue];
      }
      const filteredPickerValue = pickerValue.filter(
        (obj: any) => obj && Object.keys(obj).length > 0
      );
      inputObject[picker.apiFieldName] = filteredPickerValue;
      mandatePickerString.push(inputObject);
    }
  });

  return JSON.stringify(mandatePickerString);
};

export const postCosmosAction = async (
  raidEntity: CreateActionROBObject,
  mandateFormPickers?: MandateFormPickerConfig[]
) => {
  const tokenFromSession = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_API);

  const entityPayload: any = { ...raidEntity };

  // Attach mandatePickerFields for backend user validation when config is provided
  if (mandateFormPickers && mandateFormPickers.length > 0) {
    entityPayload.mandatePickerFields = buildMandatePickerFields(
      entityPayload.fields ?? {},
      mandateFormPickers
    );
  }

  const formData = new FormData();
  formData.append('raidEntity', JSON.stringify(entityPayload));

  const posturlurl = `${UATConstants.CommonServicesWebApiUrl}${UATConstants.ApiPaths.RaidItemCreate}${UATConstants.AllTenant['MCAPS Leader RoB'].ID}`;

  const response = await fetch(posturlurl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${tokenFromSession}`,
    },
    body: formData,
  });

  if (!response.ok) {
    let errorData: any = {};
    try {
      errorData = await response.json();
    } catch {
      // If response body cannot be parsed, fall through with empty errorData
    }
    const apiError: CreateActionApiError = {
      status: response.status,
      data: errorData,
    };
    throw apiError;
  }

  return await response.json();
};

export const postFieldRequest = (data: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
      UATConstants.ApiPaths.CreateAction,
      [],
      HttpVerbs.post,
      data,
      (response: any) => {
        resolve(response);
      },
      (errorResponse: ErrorResponse) => {
        reject(errorResponse);
      },
      false
    );
  });
};

export const postArtemisAction = createApi({
  reducerPath: 'postArtemisAction',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.CommonServicesWebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_API);
      const coreAPIToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_CORE_API);
      const graphToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_GRAPH);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      } else {
        console.warn('One or more tokens are missing');
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createRAIDItems: builder.query<any, any>({
      query: (raidEntity: any) => ({
        method: 'POST',
        url: '/api/v1/raid/transactional/raiditems/create?tenantid=' + raidEntity.tenantId,
        body: JSON.stringify(raidEntity),
      }),
    }),
  }),
});
